import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UserPlus, Phone, PhoneCall, PhoneMissed, Star, Repeat, GraduationCap, Trophy, Ban, RefreshCw, ChevronRight, X } from 'lucide-react';

const CARDS = [
  { key: 'assigned', label: 'New leads', Icon: UserPlus, tint: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  { key: 'calls', label: 'Total calls', Icon: Phone, tint: 'bg-blue-50 text-blue-600 border-blue-200' },
  { key: 'connected', label: 'Connected', Icon: PhoneCall, tint: 'bg-green-50 text-green-600 border-green-200' },
  { key: 'rnr', label: 'RNR (not connected)', Icon: PhoneMissed, tint: 'bg-red-50 text-red-600 border-red-200' },
  { key: 'first', label: 'First calls', Icon: Star, tint: 'bg-sky-50 text-sky-600 border-sky-200' },
  { key: 'followups', label: 'Follow-ups', Icon: Repeat, tint: 'bg-violet-50 text-violet-600 border-violet-200' },
  { key: 'counselling', label: 'Counselling sessions', Icon: GraduationCap, tint: 'bg-teal-50 text-teal-600 border-teal-200' },
  { key: 'conversions', label: 'Conversions', Icon: Trophy, tint: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  { key: 'bad', label: 'Bad leads', Icon: Ban, tint: 'bg-orange-50 text-orange-600 border-orange-200' },
];

export default function AdminDailyReport() {
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString('en-CA');
  const [period, setPeriod] = useState('day');
  const [date, setDate] = useState(today);
  const [staffId, setStaffId] = useState('all');
  const [staff, setStaff] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [drill, setDrill] = useState(null); // { key, label }
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const range = useCallback(() => {
    const d = new Date(date + 'T00:00:00');
    if (period === 'day') return { start: date, end: date };
    if (period === 'week') {
      const s = new Date(d); s.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      const e = new Date(s); e.setDate(s.getDate() + 6);
      return { start: s.toLocaleDateString('en-CA'), end: e.toLocaleDateString('en-CA') };
    }
    const s = new Date(d.getFullYear(), d.getMonth(), 1);
    const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { start: s.toLocaleDateString('en-CA'), end: e.toLocaleDateString('en-CA') };
  }, [period, date]);

  const qs = useCallback(() => {
    const { start, end } = range();
    const base = period === 'day' ? `date=${start}` : `start=${start}&end=${end}`;
    return staffId !== 'all' ? `${base}&user_id=${staffId}` : base;
  }, [range, period, staffId]);

  useEffect(() => {
    apiClient.get('/activity-logs/report-staff').then(r => setStaff(r.data.staff || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const { start, end } = range();
      const q = period === 'day' ? `date=${start}` : `start=${start}&end=${end}`;
      const url = staffId === 'all'
        ? `/activity-logs/team-daily-summary?${q}`
        : `/activity-logs/counselor-daily-summary/${staffId}?${q}`;
      const r = await apiClient.get(url);
      setData(r.data);
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [range, period, staffId]);
  useEffect(() => { load(); }, [load]);

  const openDrill = async (card) => {
    setDrill(card); setDrillData(null); setDrillLoading(true);
    try {
      const r = await apiClient.get(`/activity-logs/daily-drilldown?metric=${card.key}&${qs()}`);
      setDrillData(r.data);
    } catch { setDrillData({ leads: [], count: 0 }); }
    finally { setDrillLoading(false); }
  };

  const rangeLabel = () => {
    const { start, end } = range();
    const fmt = s => new Date(s + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
    return period === 'day' ? (date === today ? 'Today' : fmt(start)) : `${fmt(start)} – ${fmt(end)}`;
  };

  const c = data?.calls || {};
  const firstCalls = (c.newly_assigned_connected || 0) + (c.newly_assigned_rnr || 0);
  const followUps = (c.followup_connected || 0) + (c.followup_rnr || 0) + (c.rnr_followup_connected || 0) + (c.rnr_followup_rnr || 0);
  const totalCalls = (c.total_connected || 0) + (c.total_not_connected || 0);
  const values = {
    assigned: data?.leads_assigned?.total || 0,
    calls: totalCalls,
    connected: c.total_connected || 0,
    rnr: c.total_not_connected || 0,
    first: firstCalls,
    followups: followUps,
    counselling: data?.counselling_sessions_today || 0,
    conversions: data?.conversions_today || 0,
    bad: data?.bad_leads_today || 0,
  };

  const scopeName = staffId === 'all' ? 'Org-wide' : (staff.find(s => s.id === staffId)?.name || 'Staff');

  return (
    <div className="space-y-4" data-testid="admin-daily-report">
      <div className="flex flex-col gap-2">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-[#1B2559]" data-testid="admin-report-title">Daily report · {scopeName}</h2>
          <p className="text-xs text-slate-500">{rangeLabel()} · tap any tile to see the leads behind it</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={staffId} onChange={e => setStaffId(e.target.value)} data-testid="admin-report-staff"
            className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm max-w-[180px]">
            <option value="all">All staff (org-wide)</option>
            <optgroup label="Counsellors">
              {staff.filter(s => s.role !== 'telecaller').map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </optgroup>
            <optgroup label="Telecallers">
              {staff.filter(s => s.role === 'telecaller').map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </optgroup>
          </select>
          <div className="flex rounded-lg bg-slate-100 p-0.5" data-testid="admin-report-period">
            {[['day', 'Day'], ['week', 'Week'], ['month', 'Month']].map(([key, label]) => (
              <button key={key} onClick={() => setPeriod(key)} className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${period === key ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500'}`} data-testid={`admin-period-${key}`}>{label}</button>
            ))}
          </div>
          <input type="date" value={date} max={today} onChange={e => setDate(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="admin-report-date" />
          <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={load} disabled={loading} aria-label="Refresh" data-testid="admin-report-refresh"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </div>

      {loading ? <p className="py-8 text-center text-sm text-slate-500" data-testid="admin-report-loading">Loading numbers…</p>
        : error ? <div className="py-8 text-center" data-testid="admin-report-error"><p className="mb-2 text-sm text-slate-500">Could not load the report.</p><Button variant="outline" size="sm" onClick={load}>Retry</Button></div>
        : <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4" data-testid="admin-report-cards">
            {CARDS.map(({ key, label, Icon, tint }) => (
              <button key={key} onClick={() => openDrill({ key, label })} className={`text-left rounded-xl border p-3 transition-transform hover:scale-[1.02] active:scale-95 ${tint}`} data-testid={`admin-card-${key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold tabular-nums" data-testid={`admin-value-${key}`}>{values[key]}</span>
                  <Icon className="h-5 w-5 opacity-70" />
                </div>
                <p className="mt-1 flex items-center gap-0.5 text-[11px] font-medium text-slate-600 leading-tight">{label}<ChevronRight className="w-3 h-3 opacity-50" /></p>
              </button>
            ))}
          </div>}

      <Dialog open={!!drill} onOpenChange={o => { if (!o) setDrill(null); }}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{drill?.label} · {scopeName} · {rangeLabel()}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto -mx-2 px-2">
            {drillLoading ? <p className="py-8 text-center text-sm text-slate-500" data-testid="drilldown-loading">Loading leads…</p>
              : !drillData?.leads?.length ? <p className="py-8 text-center text-sm text-slate-500" data-testid="drilldown-empty">No leads for this tile.</p>
              : <ul className="divide-y divide-slate-100" data-testid="drilldown-list">
                  {drillData.leads.map((l, i) => (
                    <li key={l.id + i} className="flex items-center gap-2 py-2.5" data-testid={`drilldown-row-${l.id}`}>
                      <button onClick={() => { setDrill(null); navigate(`/students/${l.id}`); }} className="min-w-0 flex-1 text-left" data-testid={`drilldown-open-${l.id}`}>
                        <p className="truncate text-sm font-semibold text-[#1B2559]">{l.name}</p>
                        <p className="truncate text-[11px] text-slate-500">
                          {l.outcome && <span className="mr-1">{l.outcome}</span>}
                          {l.by && <span className="mr-1">· {l.by}</span>}
                          {l.status && <span className="rounded bg-slate-100 px-1 py-0.5">{l.status.replace(/_/g, ' ')}</span>}
                        </p>
                      </button>
                      {l.phone && <a href={`tel:${l.phone}`} onClick={e => e.stopPropagation()} className="shrink-0 rounded-full bg-green-100 p-2 text-green-700 hover:bg-green-200" aria-label={`Call ${l.name}`} data-testid={`drilldown-call-${l.id}`}><PhoneCall className="w-4 h-4" /></a>}
                    </li>
                  ))}
                </ul>}
          </div>
          <p className="pt-1 text-center text-[11px] text-slate-400">{drillData?.count || 0} lead(s) · tap a name to open the profile, or the green button to call</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
